import { useState } from "react";
import { apiPost, apiDelete } from "../lib/api";
import { COLORS } from "../lib/colors";

const API_URL = import.meta.env.VITE_API_URL || "https://api.careersorter.co";
const STATUS_ICONS = { active: "🟢", hired: "🔵", inactive: "⚪" };
const REQUEST_ICONS = { approved: "✅", pending: "⏳", declined: "❌", revoked: "🚫" };

export default function StudentList({ students, token, onRefresh }) {
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [statusEdits, setStatusEdits] = useState({});
  const [companyEdits, setCompanyEdits] = useState({});

  if (!students || students.length === 0) {
    return (
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>
          My Students
        </h3>
        <p style={{ fontSize: 13, color: COLORS.textMuted }}>No students in pool yet.</p>
      </div>
    );
  }

  const displayList = showAll ? students : students.slice(0, 10);

  const handleStatusUpdate = async (student) => {
    const newStatus = statusEdits[student.id] || student.status;
    const hiredCompany = companyEdits[student.id] || "";
    if (newStatus === student.status) return;

    setUpdating(student.id);
    try {
      await apiPost(
        `/dashboard/update_status?candidate_id=${student.id}&status=${newStatus}&hired_company=${encodeURIComponent(hiredCompany)}`,
        {},
        token
      );
      onRefresh();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Delete ${student.name}? This cannot be undone.`)) return;
    setUpdating(student.id);
    try {
      await apiDelete(`/dashboard/delete_student?candidate_id=${student.id}`, token);
      onRefresh();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>
          My Students
        </h3>
        <label style={{ fontSize: 13, color: COLORS.textSecondary, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={e => setShowAll(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Show all {students.length}
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {displayList.map(s => {
          const isExpanded = expandedId === s.id;
          const currentStatus = statusEdits[s.id] || s.status;
          const viewsLabel = s.views > 0 ? `${s.views}` : "0";
          const reqLabel = (s.cv_requests || 0) > 0 ? ` · ${s.cv_requests} requests` : "";

          return (
            <div key={s.id} style={{
              backgroundColor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              overflow: "hidden",
            }}>
              {/* Header */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : s.id)}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{STATUS_ICONS[s.status] || "⚪"}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                    {s.name}
                  </span>
                  <span style={{ fontSize: 13, color: COLORS.textMuted }}>
                    — {s.programme}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                  👁 {viewsLabel} views{reqLabel} {isExpanded ? "−" : "+"}
                </span>
              </div>

              {/* Expanded */}
              {isExpanded && (
                <div style={{
                  padding: "0 16px 16px",
                  borderTop: `1px solid ${COLORS.borderLight}`,
                  paddingTop: 14,
                }}>
                  <div className="student-detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {/* Left: Info */}
                    <div>
                      <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 6 }}>
                        <strong>Degree:</strong> {s.degree}
                      </p>
                      <a
                        href={`${API_URL}/view_cv/${s.id}?token=${encodeURIComponent(token || "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          padding: "5px 12px",
                          borderRadius: 6,
                          backgroundColor: COLORS.accent,
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: "none",
                          marginBottom: 8,
                        }}
                      >
                        View CV
                      </a>
                      {s.hired_company && (
                        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 6 }}>
                          <strong>Hired at:</strong> {s.hired_company}
                        </p>
                      )}
                      {(s.cv_requests || 0) > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 4 }}>
                            <strong>CV Requests:</strong> {s.cv_requests} (✅ {s.cv_approved || 0} · ⏳ {s.cv_pending || 0})
                          </p>
                          {(s.cv_request_details || []).map((req, i) => (
                            <p key={i} style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 2 }}>
                              {REQUEST_ICONS[req.status] || "❓"} {req.company || "—"} ({req.recruiter_email || "Unknown"}) · {req.date || ""}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right: Viewers */}
                    <div>
                      {s.viewers && s.viewers.length > 0 ? (
                        <>
                          <p style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>
                            Viewed by:
                          </p>
                          {s.viewers.map((v, i) => (
                            <p key={i} style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 2 }}>
                              👁 {v.email} · {v.date}
                            </p>
                          ))}
                        </>
                      ) : (
                        <p style={{ fontSize: 12, color: COLORS.textMuted }}>No views yet</p>
                      )}
                    </div>
                  </div>

                  {/* Status update */}
                  <div style={{
                    display: "flex", alignItems: "flex-end", gap: 12,
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: `1px solid ${COLORS.borderLight}`,
                  }}>
                    <div>
                      <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>
                        Status
                      </label>
                      <select
                        value={currentStatus}
                        onChange={e => setStatusEdits({ ...statusEdits, [s.id]: e.target.value })}
                        style={{
                          padding: "6px 10px", borderRadius: 6,
                          border: `1px solid ${COLORS.border}`,
                          fontSize: 13, fontFamily: "inherit",
                          backgroundColor: COLORS.surface,
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="hired">Hired</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {currentStatus === "hired" && (
                      <div>
                        <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>
                          Company
                        </label>
                        <input
                          type="text"
                          value={companyEdits[s.id] ?? (s.hired_company || "")}
                          onChange={e => setCompanyEdits({ ...companyEdits, [s.id]: e.target.value })}
                          placeholder="Company name"
                          style={{
                            padding: "6px 10px", borderRadius: 6,
                            border: `1px solid ${COLORS.border}`,
                            fontSize: 13, fontFamily: "inherit",
                            backgroundColor: COLORS.surface,
                          }}
                        />
                      </div>
                    )}

                    {currentStatus !== s.status && (
                      <button
                        onClick={() => handleStatusUpdate(s)}
                        disabled={updating === s.id}
                        style={{
                          padding: "6px 14px", borderRadius: 6, border: "none",
                          backgroundColor: COLORS.accent, color: "#fff",
                          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                          cursor: updating === s.id ? "wait" : "pointer",
                          opacity: updating === s.id ? 0.7 : 1,
                        }}
                      >
                        Update
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(s)}
                      disabled={updating === s.id}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "none",
                        backgroundColor: COLORS.redLight, color: COLORS.red,
                        fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                        cursor: updating === s.id ? "wait" : "pointer",
                        marginLeft: "auto",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
